// Plain-language help for the Codex policy controls.
// Labels, purposes, selected outcomes, and interactions. Input examples and
// conditional workflow guidance live in controlGuidance.jsx.
//
// Sources: the server catalog descriptions, the Codex config reference and
// enterprise requirements pages. A sentence that we could not confirm says
// so.

import { valueAt } from "./document.js";

export const optionLabel = (value) => {
    const words = String(value).replaceAll("_", " ").replaceAll("-", " ");
    return words[0].toUpperCase() + words.slice(1);
};

const P = Object.freeze({
    approvals: "/allowed_approval_policies",
    reviewers: "/allowed_approvals_reviewers",
    webSearch: "/allowed_web_search_modes",
    reviewerPolicy: "/guardian_policy_config",
    reviewerExtra: "/guardian_extra_policy",
    prefixRules: "/rules/prefix_rules",
    instructions: "/additional_developer_instructions",
    mcp: "/mcp_servers",
    marketplaceRestrict: "/marketplaces/restrict_to_allowed_sources",
    marketplaceSources: "/marketplaces/allowed_sources",
    features: "/features",
    hooksOnly: "/allow_managed_hooks_only",
    loginMethods: "/allowed_login_methods",
    workspaces: "/allowed_chatgpt_workspaces",
    model: "/models/new_thread/model",
    effort: "/models/new_thread/model_reasoning_effort",
    tier: "/models/new_thread/service_tier",
    remoteControl: "/allow_remote_control",
    updateCheck: "/check_for_update_on_startup",
});
export const POINTERS = P;

/// Codex feature names in the groups the Features category shows, one
/// subsection each. Every pinnable name is in exactly one group.
export const FEATURE_GROUPS = Object.freeze([
    { id: "tools", label: "Tools and execution", orientation: "Command execution, browser and desktop control, and image tools.", names: ["shell_tool", "unified_exec", "unified_exec_tty", "write_stdin_approval", "shell_snapshot", "view_image", "image_generation", "browser_use", "browser_use_external", "browser_use_full_cdp_access", "computer_use", "in_app_browser", "code_mode_host"] },
    { id: "collaboration", label: "Review and collaboration", orientation: "Automatic review, multiple agents, goals, and memory.", names: ["guardian_approval", "guardian_reuse_parent_compaction", "multi_agent", "multi_agent_v2", "goals", "mentions_v2", "memories", "tool_suggest", "sleep_tool"] },
    { id: "extensions", label: "Apps, plugins, and skills", orientation: "Apps, plugin loading and sharing, and skill tools.", names: ["apps", "plugins", "remote_plugin", "plugin_sharing", "recommended_plugins", "skill_search", "skill_mcp_dependency_install", "tool_call_mcp_elicitation", "auth_elicitation"] },
    { id: "client", label: "Client and performance", orientation: "Desktop app features, updates, and request performance.", names: ["fast_mode", "enable_request_compression", "compaction_image_budget", "daemon_auto_start", "in_app_chat", "in_app_dictation", "in_app_local_automation", "in_app_updates", "realtime_conversation", "unbounded_connection_retries", "workspace_dependencies", "worktrees"] },
]);

/// Label and optional purpose. Input formats and conditional impacts live in controlGuidance.
export const CODEX_COPY = Object.freeze({
    [P.approvals]: ["Approval choices", "Allowed approval policies. Commands follow the selected policy."],
    [P.reviewers]: ["Approval reviewers", ""],
    [P.webSearch]: ["Web search choices", "Shell network access is controlled separately."],
    [P.reviewerPolicy]: ["Automatic reviewer policy", "Instructions for the review model. Use command rules to enforce blocks."],
    [P.reviewerExtra]: ["Additional reviewer policy", ""],
    [P.prefixRules]: ["Command prefix rules", ""],
    [P.instructions]: ["Developer instructions", "Instructions for every Codex session. Use permission settings to enforce restrictions."],
    [P.mcp]: ["MCP server identities", "The server name and its URL or command must match."],
    [P.marketplaceRestrict]: ["Restrict marketplace sources", ""],
    [P.marketplaceSources]: ["Allowed marketplace sources", "Requires marketplace restrictions to be on."],
    [P.features]: ["Feature settings", ""],
    [P.hooksOnly]: ["Block user, repository, and plugin hooks", ""],
    [P.loginMethods]: ["Sign-in methods", ""],
    [P.workspaces]: ["ChatGPT workspaces", ""],
    [P.model]: ["Starting model", "Users can override this default."],
    [P.effort]: ["Starting reasoning effort", ""],
    [P.tier]: ["Starting service tier", ""],
    [P.remoteControl]: ["Remote Control", ""],
    [P.updateCheck]: ["Startup update check", ""],
});

export const CHOICE_DETAILS = Object.freeze({
    [P.approvals]: {
        untrusted: "People cannot pick it. Codex uses it by itself in a project the person marked untrusted.",
        "on-request": "Commands that need approval go to the configured reviewer.",
        never: "Codex never requests approval. Commands that need approval cannot run.",
        granular: "Codex asks for some kinds of actions and not others, as the person configures.",
    },
    [P.reviewers]: {
        user: "The person answers approval prompts.",
        auto_review: "A second model answers approval prompts, following your reviewer policy.",
    },
    [P.webSearch]: {
        disabled: "No web search tool. Always available.",
        cached: "Results from an index OpenAI maintains. No live request to other sites.",
        indexed: "Live requests only for pages the index already knows.",
        live: "Unrestricted web requests.",
    },
    [P.loginMethods]: {
        chatgpt: "Sign in with a ChatGPT account.",
        api: "Sign in with an API key, including Amazon Bedrock.",
    },
    [P.effort]: {
        ultra: "Requires model support.",
        persistent: "Retains reasoning across turns. Requires model support.",
    },
    [P.tier]: {
        fast: "Lower latency, higher usage.",
        flex: "Lower cost, higher latency. May wait for capacity.",
    },
});

const NOT_CONFIGURED = "No value is imposed by this policy. Other configuration still applies.";

/// Descriptions for the three cards of a yes/no setting.
export const BOOL_DETAILS = Object.freeze({
    [P.marketplaceRestrict]: {
        unset: NOT_CONFIGURED,
        on: "Plugins may only be installed from the sources listed below.",
        off: "Any marketplace source may be added. Same as writing nothing, but the person cannot turn the restriction on.",
    },
    [P.hooksOnly]: {
        unset: NOT_CONFIGURED,
        on: "Hook files in the person's home folder, the project, and plugins are ignored.",
        off: "Those hook files run as usual. Same as writing nothing, but the person cannot change it.",
    },
    [P.remoteControl]: {
        unset: NOT_CONFIGURED,
        on: "This policy does not restrict Remote Control.",
        off: "Remote Control is turned off in the Codex app server.",
    },
    [P.updateCheck]: {
        unset: NOT_CONFIGURED,
        on: "The CLI checks for a new version at startup and tells the person.",
        off: "The CLI does not check. Nothing is installed either way.",
    },
});

const isList = (value) => Array.isArray(value);
const isMap = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const count = (n, noun, plural = `${noun}s`) => `${n} ${n === 1 ? noun : plural}`;
const names = (list) => list.map(optionLabel).join(", ");
const joinOr = (list) => (list.length <= 1 ? list.join("") : `${list.slice(0, -1).join(", ")} or ${list.at(-1)}`);

/// What a managed session does under the current selection. `written` is true
/// when the selection puts a key in the document.
export function onEndpoints(pointer, document) {
    const value = valueAt(document, pointer);
    switch (pointer) {
        case P.approvals: {
            if (!isList(value) || !value.length) return { written: false, text: "This policy adds no approval-policy restriction. Other configuration still applies." };
            const selectable = value.filter((item) => item !== "untrusted");
            if (selectable.length === 1 && selectable[0] === "never") return { written: true, text: "Codex never asks for approval on managed machines. A session that asks for another style is moved to Never." };
            return { written: true, text: `People can choose ${joinOr(selectable.map(optionLabel))}. A session that asks for another style is moved to ${optionLabel(value[0])}.` };
        }
        case P.reviewers: {
            if (!isList(value) || !value.length) return { written: false, text: "This policy adds no reviewer restriction. Other configuration selects the reviewer." };
            if (value.includes("user") && value.includes("auto_review")) return { written: true, text: "Both human and automatic reviewers are permitted. The active configuration selects the reviewer." };
            if (value.includes("user")) return { written: true, text: "The person answers every approval prompt. Automatic review cannot be turned on." };
            return { written: true, text: "Automatic review answers approval prompts. The person cannot be set as the reviewer." };
        }
        case P.webSearch: {
            if (!isList(value) || !value.length) return { written: false, text: "This policy adds no web search restriction. Other configuration still applies." };
            const chosen = value.filter((item) => item !== "disabled");
            if (!chosen.length) return { written: true, text: "Web search is off. No session can turn it on." };
            return { written: true, text: `People can choose ${joinOr(chosen.map(optionLabel))}. Disabled is always available.` };
        }
        case P.reviewerPolicy:
            if (!value) return { written: false, text: "Nothing is written. Automatic review uses the person's own policy text, if they wrote one." };
            return { written: true, text: "When automatic review decides a request, it reads this text. The person's own reviewer policy is replaced." };
        case P.reviewerExtra:
            if (!value) return { written: false, text: "Nothing is written. Automatic review uses the person's own extra text, if they wrote one." };
            return { written: true, text: "Automatic review reads this text alongside the main policy. The person's own extra text is replaced." };
        case P.prefixRules: {
            if (!isList(value) || !value.length) return { written: false, text: "This policy adds no command rules. Existing rules and approval settings still apply." };
            const blocked = value.filter(rule => rule.decision === "forbidden").length;
            const prompted = value.filter(rule => rule.decision === "prompt").length;
            return { written: true, text: `${blocked ? "Commands matching a Block rule are refused. " : ""}${prompted ? "Commands matching a Require approval rule need approval through the configured reviewer. " : ""}The most restrictive match wins. Unmatched commands still follow other rules and approval settings.` };
        }
        case P.instructions:
            if (!value) return { written: false, text: "This policy adds no developer instructions. Other instruction sources still apply." };
            return { written: true, text: `${value.length.toLocaleString()} characters are added to every session as a message from your organization. The model reads it. It does not block anything.` };
        case P.mcp: {
            if (!isMap(value)) return { written: false, text: "This policy adds no MCP server restriction. Other configuration still applies." };
            if (!Object.keys(value).length) return { written: true, text: "All MCP servers are disabled by this policy." };
            return { written: true, text: `${count(Object.keys(value).length, "server identity", "server identities")}. A configured server loads only if its name and its command or URL match one of them. Every other server loads disabled.` };
        }
        case P.marketplaceRestrict:
            if (value === true) return { written: true, text: "Plugins may only be installed from the sources listed below." };
            if (value === false) return { written: true, text: "Any marketplace source may be added, and the person cannot turn the restriction on." };
            return { written: false, text: "This policy adds no marketplace restriction. Other configuration still applies." };
        case P.marketplaceSources: {
            const n = isMap(value) ? Object.keys(value).length : 0;
            const on = valueAt(document, P.marketplaceRestrict) === true;
            if (!n) return { written: false, text: "No sources are listed." };
            return { written: true, text: `${count(n, "source")} ${n === 1 ? "is" : "are"} listed. ${on ? "Plugins may only be installed from them." : "They have no effect until Restrict marketplace sources is On."}` };
        }
        case P.hooksOnly:
            if (value === true) return { written: true, text: "Hook files in the person's home folder, the project, the session, and plugins are ignored." };
            if (value === false) return { written: true, text: "Hook files run as usual, and the person cannot change that." };
            return { written: false, text: "This policy adds no hook-source restriction. Other configuration still applies." };
        case P.loginMethods: {
            if (!isList(value) || !value.length) return { written: false, text: "This policy adds no sign-in method restriction. Other authentication requirements still apply." };
            if (value.length >= 2) return { written: true, text: "People can sign in with a ChatGPT account or an API key. Nobody needs both." };
            return { written: true, text: value[0] === "chatgpt" ? "People can sign in only with a ChatGPT account." : "People can sign in only with an API key." };
        }
        case P.workspaces: {
            if (!isList(value)) return { written: false, text: "This policy adds no workspace restriction. Other authentication requirements still apply." };
            if (!value.length) return { written: true, text: "ChatGPT sign-in is turned off. API sign-in still works if Sign-in methods allows it." };
            return { written: true, text: `ChatGPT sign-in works only in ${count(value.length, "listed workspace")}.` };
        }
        case P.model:
            if (!value) return { written: false, text: "No starting model is imposed by this policy. Other configuration and Codex defaults apply." };
            return { written: true, text: `New threads start with ${value}. A model chosen on the command line or in a profile overrides it.` };
        case P.effort:
            if (!value) return { written: false, text: "Nothing is written. New threads start with the model's default effort." };
            return { written: true, text: `New threads start with ${optionLabel(value)} effort. An effort or model chosen on the command line or in a profile overrides it.` };
        case P.tier:
            if (!value) return { written: false, text: "Nothing is written. New threads start with the account's default tier." };
            return { written: true, text: `New threads start on the ${optionLabel(value)} tier. A tier chosen on the command line or in a profile overrides it.` };
        case P.remoteControl:
            if (value === false) return { written: true, text: "Remote Control is turned off in the Codex app server on managed machines." };
            if (value === true) return { written: true, text: "This policy does not restrict Remote Control." };
            return { written: false, text: "Nothing is written. Remote Control follows the person's own setting." };
        case P.updateCheck:
            if (value === true) return { written: true, text: "The CLI checks for a new version at startup." };
            if (value === false) return { written: true, text: "The CLI does not check for a new version at startup." };
            return { written: false, text: "Nothing is written. The CLI keeps its own update check behavior." };
        default:
            return null;
    }
}

/// What managed sessions do for one feature group under the current selection.
export function featureGroupOnEndpoints(group, document) {
    const value = valueAt(document, P.features);
    const pinned = isMap(value) ? Object.entries(value).filter(([name]) => group.names.includes(name)) : [];
    if (!pinned.length) return { written: false, text: "No feature in this group is pinned. People keep their own settings for these features." };
    const on = pinned.filter(([, enabled]) => enabled).length;
    return { written: true, text: `${count(pinned.length, "feature")} pinned: ${on} on, ${pinned.length - on} off. A person whose own settings disagree gets a configuration error until it is resolved.` };
}

/// Lines that appear only when two settings collide. Each is attached to the
/// pointer it is shown under and names the other control.
export function interactions(pointer, document) {
    const approvals = valueAt(document, P.approvals);
    const reviewers = valueAt(document, P.reviewers);
    const logins = valueAt(document, P.loginMethods);
    const workspaces = valueAt(document, P.workspaces);
    const onlyNever = isList(approvals) && approvals.filter((item) => item !== "untrusted").join() === "never";
    const autoReviewDenied = isList(reviewers) && reviewers.length > 0 && !reviewers.includes("auto_review");
    const reviewerTextWritten = Boolean(valueAt(document, P.reviewerPolicy) || valueAt(document, P.reviewerExtra));
    const chatgptDenied = isList(logins) && logins.length > 0 && !logins.includes("chatgpt");
    const chatgptOnly = isList(logins) && logins.length === 1 && logins[0] === "chatgpt";
    const out = [];
    const add = (scope, target, text) => out.push({ scope, target, text });
    switch (pointer) {
        case P.approvals:
            if (onlyNever && isList(reviewers) && reviewers.length) add("Approval reviewers", P.reviewers, "Never is the only approval style allowed, so Codex never asks. The reviewer setting has no effect.");
            break;
        case P.reviewers:
            if (onlyNever && isList(reviewers) && reviewers.length) add("Approval choices", P.approvals, "Never is the only approval style allowed, so Codex never asks. This setting has no effect until another style is allowed.");
            if (autoReviewDenied && reviewerTextWritten) add("Automatic reviewer policy", P.reviewerPolicy, "Auto review is not allowed here, so the reviewer policy text is never read.");
            break;
        case P.reviewerPolicy:
        case P.reviewerExtra:
            if (autoReviewDenied && reviewerTextWritten) add("Approval reviewers", P.reviewers, "Auto review is not allowed under Approval reviewers, so this text is never read.");
            if (onlyNever && reviewerTextWritten) add("Approval choices", P.approvals, "Never is the only approval style allowed, so Codex never asks and this text is never read.");
            break;
        case P.marketplaceRestrict: {
            const sources = valueAt(document, P.marketplaceSources);
            if (valueAt(document, P.marketplaceRestrict) === true && !(isMap(sources) && Object.keys(sources).length)) add("Allowed marketplace sources", P.marketplaceSources, "The restriction is on and no sources are listed. Every marketplace is blocked.");
            break;
        }
        case P.marketplaceSources: {
            const sources = valueAt(document, P.marketplaceSources);
            if (isMap(sources) && Object.keys(sources).length && valueAt(document, P.marketplaceRestrict) !== true) add("Restrict marketplace sources", P.marketplaceRestrict, "These sources do nothing until Restrict marketplace sources is On.");
            break;
        }
        case P.loginMethods:
            if (chatgptDenied && isList(workspaces)) add("ChatGPT workspaces", P.workspaces, "ChatGPT sign-in is not allowed here, so the workspace list has no effect.");
            if (chatgptOnly && isList(workspaces) && !workspaces.length) add("ChatGPT workspaces", P.workspaces, "ChatGPT is the only sign-in method allowed here, and ChatGPT sign-in is turned off under ChatGPT workspaces. Nobody can sign in.");
            break;
        case P.workspaces:
            if (chatgptDenied && isList(workspaces)) add("Sign-in methods", P.loginMethods, "ChatGPT sign-in is not allowed under Sign-in methods, so this list has no effect.");
            if (chatgptOnly && isList(workspaces) && !workspaces.length) add("Sign-in methods", P.loginMethods, "ChatGPT sign-in is turned off here, and it is the only method allowed under Sign-in methods. Nobody can sign in.");
            break;
        default:
            break;
    }
    return out;
}

export function describeMarketplaceSource(source) {
    if (source?.url) return source.ref ? `Revision ${source.ref} of ${source.url}.` : `Any revision of ${source.url}.`;
    if (source?.host_pattern) return `Any repository on a git host matching ${source.host_pattern}.`;
    if (source?.path) return `The folder ${source.path} on the machine.`;
    return "Unrecognized source.";
}

const describeMatcher = (matcher, noun) => {
    if (typeof matcher === "string") return `${noun} is exactly ${matcher}`;
    if (matcher?.match === "prefix") return `${noun} starts with ${matcher.value}`;
    if (matcher?.match === "regex") return `${noun} matches the pattern ${matcher.expression}`;
    if (matcher?.match === "exact") return `${noun} is exactly ${matcher.value}`;
    return `${noun} is unrecognized`;
};

/// English for one MCP server identity.
export function describeMcpIdentity(name, source) {
    const identity = source?.identity ?? {};
    if (identity.url !== undefined) return `The server named ${name} loads only when its ${describeMatcher(identity.url, "URL")}.`;
    if (identity.command !== undefined) {
        const command = identity.command;
        if (typeof command === "string") return `The server named ${name} loads only when its command is exactly ${command}. Arguments are not checked.`;
        const args = (command.args ?? []).map((argument, index) => describeMatcher(argument, `argument ${index + 1}`));
        return `The server named ${name} loads only when its executable is ${command.executable}${args.length ? ` and ${args.join(", ")}` : ""}.`;
    }
    return `The server named ${name} has an identity this page cannot describe.`;
}
